/**
 * Shared availability helper — single source of truth for which units are
 * bookable for a given date range. Used by CalendarTab (auto-block stripes)
 * and NewReservation (unit-picker filtering). The backend applies the same
 * direct-only rules in `pms_workspace._check_availability` so the two
 * surfaces never diverge.
 *
 * Direct-only semantics (host spec Feb 2026, §3):
 *   links A↔B and B↔C means:
 *     • Booking A blocks A + B only  (NOT C)
 *     • Booking B blocks B + A + C   (both of B's direct neighbours)
 *     • Booking C blocks C + B only  (NOT A)
 *   Chains are NOT traversed.
 */

/** Build an adjacency map from the flat `pms_links` list.
 *  Returns Map<unitId, Set<directNeighbourId>>. */
export function buildDirectNeighbours(unitLinks = []) {
  const adj = new Map();
  for (const l of unitLinks) {
    if (!adj.has(l.a)) adj.set(l.a, new Set());
    if (!adj.has(l.b)) adj.set(l.b, new Set());
    adj.get(l.a).add(l.b);
    adj.get(l.b).add(l.a);
  }
  return adj;
}

/** Direct-only neighbour set of `uid` (includes uid itself). */
export function directGroup(uid, adj) {
  const s = new Set([uid]);
  const n = adj.get(uid);
  if (n) for (const x of n) s.add(x);
  return s;
}

/** Half-open interval overlap, same as backend `_ranges_overlap`. */
export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Which units are unavailable for [ci, co)?
 * Returns Map<unitId, Array<conflictEntry>>.
 *
 *   bookings     : [{unit_id, checkin, checkout, status, guest, id}]
 *   manualBlocks : [{unit_id, start, end, reason}]
 *   unitLinks    : [{a, b}]    (raw pms_links rows)
 *   ci, co       : ISO date strings (half-open, same as backend)
 */
export function computeUnitConflicts({ bookings = [], manualBlocks = [], unitLinks = [], ci, co }) {
  const adj = buildDirectNeighbours(unitLinks);
  const conflicts = new Map();
  const add = (uid, entry) => {
    const arr = conflicts.get(uid) || [];
    arr.push(entry);
    conflicts.set(uid, arr);
  };

  // (a) direct overlapping bookings (non-cancelled) on each unit
  for (const b of bookings) {
    if (!b.unit_id) continue;
    const st = (b.status || "").toLowerCase();
    // Must match backend `_check_availability` which filters
    // {"$nin": ["cancelled", "no_show"]}. Keep the two in sync — if a
    // cancelled booking leaks through here it will keep blocking the
    // picker even after the row is cancelled.
    if (st === "cancelled" || st === "no_show") continue;
    if (!rangesOverlap(b.checkin, b.checkout, ci, co)) continue;
    add(b.unit_id, { checkin: b.checkin, checkout: b.checkout,
                     guest: b.guest, id: b.id, reason: "booked" });
    // (b) propagate to DIRECT neighbours only — NOT transitive
    for (const nb of (adj.get(b.unit_id) || [])) {
      add(nb, { checkin: b.checkin, checkout: b.checkout,
                guest: b.guest, id: b.id,
                reason: "connected-unit-booked", via: b.unit_id });
    }
  }

  // (c) manual date blocks + propagate to direct neighbours
  for (const bl of manualBlocks) {
    if (!bl.unit_id) continue;
    if (!rangesOverlap(bl.start, bl.end, ci, co)) continue;
    add(bl.unit_id, { checkin: bl.start, checkout: bl.end,
                      guest: bl.reason || "Manual block", reason: "blocked" });
    for (const nb of (adj.get(bl.unit_id) || [])) {
      add(nb, { checkin: bl.start, checkout: bl.end,
                guest: bl.reason || "Manual block",
                reason: "connected-unit-blocked", via: bl.unit_id });
    }
  }
  return conflicts;
}

/**
 * Lightweight per-day map variant used by CalendarTab for striped-cell
 * rendering. Returns {autoBookedDays, autoBlockedDays} keyed by
 * `${unitId}-${dayNumber}` so the row renderer can look them up in O(1).
 *
 *   bookings / blocks : already clamped to the current month's day numbers
 *                       via the caller's clampDay helper.
 */
export function computeAutoBlockedDays({ bookingsClamped = [], blocksClamped = [], unitLinks = [] }) {
  const adj = buildDirectNeighbours(unitLinks);
  const autoBookedDays = {};
  const autoBlockedDays = {};
  for (const { b, startDay, endDay } of bookingsClamped) {
    const neighbours = adj.get(b.unit_id);
    if (!neighbours || neighbours.size === 0) continue;
    for (let dd = startDay; dd < endDay; dd++) {
      for (const nb of neighbours) autoBookedDays[`${nb}-${dd}`] = b;
    }
  }
  for (const { bl, startDay, endDay } of blocksClamped) {
    const neighbours = adj.get(bl.unit_id);
    if (!neighbours || neighbours.size === 0) continue;
    for (let dd = startDay; dd < endDay; dd++) {
      for (const nb of neighbours) autoBlockedDays[`${nb}-${dd}`] = bl;
    }
  }
  return { autoBookedDays, autoBlockedDays };
}
